import { useState } from "react";
import { useProductUpdate } from "@/services/useProductUpdate";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import moment from "moment";

const pictoDescriptions = [
  { src: "/onair/oa-link.svg", alt: "LINK OA" },
  { src: "/onair/free-network.svg", alt: "FREE NETWORK" },
  { src: "/onair/oa-figure.svg", alt: "FIGURE" },
  { src: "/onair/picto_fontaine.png", alt: "FONTAINE" },
  { src: "/onair/picto_sismo.png", alt: "SISMO" },
  { src: "/onair/picto_vis_body.png", alt: "OPTIONS" },
];

type ProductState = {
    name: string;
    order: string;
    price_1: string;
    price_2: string;
    price_3: string;
    price_4: string;
    txt: string;
    needSepa: boolean;
    feature_0: string;
    feature_1: string;
    feature_2: string;
    feature_3: string;
    feature_4: string;
    feature_5: string;
    enabled: boolean;
    top_offer: boolean;
  };


export default function ProductOnAirFrance({ place, product, validFrom, validThrough }) {
  const { mutate } = useProductUpdate({
    productId: product.id,
    placeId: place.msdsId,
  });
  const [state, stateSet] = useState<ProductState>({
    name: product.description?.name || "",
    order: product.description?.order || "",
    price_1: product.description?.price_1 || "",
    price_2: product.description?.price_2 || "",
    price_3: product.description?.price_3 || "",
    price_4: product.description?.price_4 || "",
    txt: product.description?.txt || "",
    needSepa: product.description?.needSepa || false,
    feature_0: product.description?.feature_0 || "",
    feature_1: product.description?.feature_1 || "",
    feature_2: product.description?.feature_2 || "",
    feature_3: product.description?.feature_3 || "",
    feature_4: product.description?.feature_4 || "",
    feature_5: product.description?.feature_5 || "",
    enabled: !!product.enabled,
    top_offer: !!product.top_offer,
  });

  const save = (e) => {
    e.preventDefault();
    if (!window.confirm(`Vous allez modifier le produit ${product.data.name}`)) return;

    const { enabled, top_offer, name, ...rest } = state;
    mutate({
      description: { name, ...rest },
      enabled,
      top_offer,
      name,
      type: product.type || "",
      subscription_type_id: product.subscription_type_id ?? null
    });
  };

  const onChange = (e) => {
    stateSet((prev) => {
      if (e.target.name === "needSepa") return { ...prev, needSepa: !prev.needSepa };
      return { ...prev, [e.target.name]: e.target.value };
    });
  };

  const onCheckedChange = (value) => {
    stateSet({ ...state, enabled: value });
  };

  const onCheckedChangeTopOffer = (value) => {
    stateSet({ ...state, top_offer: value });
  };

  return (
    <Card className="mb-8 ml-4">
      <CardHeader>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <CardTitle>{product.data.name}</CardTitle>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div style={{ display: "flex", alignItems: "left" }}>
              <Switch checked={state.enabled} onCheckedChange={onCheckedChange} />
              <span className="ml-2">Actif pour ce club</span>
            </div>
            {product.data.type == "subscription" && ( 
              <div style={{ display: "flex", alignItems: "left", marginTop: "0.5rem" }}>
                <Switch
                  checked={state.top_offer}
                  onCheckedChange={onCheckedChangeTopOffer}
                />
                <span className="ml-2">Offre "Best seller"</span>
              </div>
            )} 
          </div>
        </div>
        <CardDescription>
          {product.data["@id"]}{" "}
          {product.data.offer.validFrom && (
            <span className={`${!validFrom ? "text-red-500" : ""}`}>
              | Valide à partir du {moment(product.data.offer.validFrom).format("DD/MM/YYYY") + " "}{" "}
            </span>
          )}
          {product.data.offer.validThrough && (
            <span className={`${!validThrough ? "text-red-500" : ""}`}>
              jusqu'au {moment(product.data.offer.validThrough).format("DD/MM/YYYY")}
            </span>
          )}
          {!!product.otherClubs.length && (
            <>
              <p>Présente sur d'autres clubs ({product.otherClubs.length})</p>
              <div className="grid grid-cols-4 ml-4">
                {product.otherClubs.map((p) => (
                  <p className="text-xs" key={p}>
                    {p}
                  </p>
                ))}
              </div>
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex gap-6">
        <div className="w-1/2">
          <form className="flex flex-col gap-4">
            <div className="grid w-full gap-1.5">
              <Label htmlFor="name">Nom</Label>
              <Input onChange={onChange} name="name" type="text" value={state.name} />
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="order">Ordre</Label>
              <Input onChange={onChange} name="order" type="text" value={state.order} />
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="price_1">Prix unitaire</Label>
              <Input onChange={onChange} name="price_1" type="text" value={state.price_1} />
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="price_2">Prix décimal</Label>
              <Input onChange={onChange} name="price_2" type="text" value={state.price_2} />
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="price_3">Prix récurrence</Label>
              <Input onChange={onChange} name="price_3" type="text" value={state.price_3} />
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="price_4">Prix explication</Label>
              <Input onChange={onChange} name="price_4" type="text" value={state.price_4} />
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="txt">Message</Label>
              <Textarea onChange={onChange} name="txt" placeholder="Prix text" value={state.txt} />
            </div>

            <div className="items-top flex space-x-2">
              <Checkbox
                checked={state.needSepa}
                onCheckedChange={() => {
                  stateSet((prev) => {
                    return { ...prev, needSepa: !prev.needSepa };
                  });
                }}
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="terms1"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Need sepa
                </Label>
              </div>
            </div>
          </form>
        </div>

        <div className="w-1/2 pt-5">
          <form className="flex flex-col justify-between h-full">
            {pictoDescriptions.map((picto, index: number) => {
              return (
                <div key={index} className="flex">
                  <div className="h-7 w-7 mr-2">
                    <img src={picto.src} alt={picto.alt} className="h-7" />
                  </div>
                  <Textarea name={`feature_${index}`} onChange={onChange} value={state[`feature_${index}`]} />
                </div>
              );
            })}
          </form>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button className="cursor-pointer bg-[#7855EF] text-white hover:bg-[#7855EF] hover:underline" onClick={save}>
          Sauvegarder
        </Button>
      </CardFooter>
    </Card>
  );
}
